import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import { FormField, CreateUserRequest, ErrorMode } from '@/models';
import { formatPhoneNumber, isValidPhoneNumber, makeFormField, stripLeadingOnesAndZeros, stripNonNumerics } from '@/helpers';
import { CountryCode } from '@/store/ui';
import { TwoStepVerificationEvent } from '@/services/analytics/events';
import { AnalyticsEventType } from '@/services/analytics/platforms/adobe-analytics/models';

export interface VerifyMobileNumberArgs {
  request: CreateUserRequest;
  country: string;
}

interface UpdateMobileNumberForm {
  [key: string]: FormField<string | boolean | null>;
  updatedMobileNumber: FormField;
}

const UPDATE_MOBILE_NUMBER_FORM: UpdateMobileNumberForm = {
  updatedMobileNumber: makeFormField(),
};

@Component({
  name: 'CmgTwoStepVerificationWelcomeModal',
})
export default class CmgTwoStepVerificationWelcomeModal extends Vue {
  isSendingCode = false;
  isFormValid = true;
  updateMobileNumberForm = UPDATE_MOBILE_NUMBER_FORM;
  cannotReceiveCode = false;

  @Prop({ default: true, type: Boolean })
  navigationAlert!: boolean;

  get modalImageSrc(): string {
    return this.currentCountry == CountryCode.US ? this.$appStore.state.cms.images.logos.medallionReward.default 
            : this.$appStore.state.cms.images.logos.medallion.default;
  }

  get selectedUpdateMobileNumber(): boolean {
    return this.$appStore.state.ui.selectedUpdateMobileNumberOnLogin;
  }

  get currentCountry(): string {
    return this.$appStore.state.ui.countryProfile.countryCode;
  }

  get isUKCountry(): boolean {
    return this.currentCountry === CountryCode.GB;
  }

  get visible(): boolean {
    return this.$appStore.state.ui.modals.twoStepVerificationWelcome.visible;
  }

  get legalCopy(): string {
    return this.$services.config.getLegalText().createAnAccount2sv;
  }

  get currentPhoneNumber(): string {
    return this.args?.oldPhoneNumber != '' ? this.args!.oldPhoneNumber : this.args?.phoneNumber ?? '';
  }

  get maskedPhoneNumber(): string {
    const mobileNumberLastFour = this.currentPhoneNumber.length == 4 ? this.currentPhoneNumber : this.currentPhoneNumber.slice(-4);
    return '***-***-' + mobileNumberLastFour;
  }

  get phoneNumberText() {
    return this.currentCountry !== CountryCode.GB 
      ? 'Enter a valid mobile number that can accept SMS messages.' 
      : 'This field can only be changed to a valid UK Mobile Number that can accept SMS messages.';
  }
  
  get phoneInputMaxLength(): number {
    return this.updateMobileNumberForm.updatedMobileNumber.formatted ? 14 : 11;
  }

  get cannotReceiveCodeText(): string {
    return 'I don’t have a mobile number or I’m unable to receive the verification code.';
  }

  toggleCannotReceiveCode(): void {
    this.cannotReceiveCode = !this.cannotReceiveCode;
  }

  get form(): UpdateMobileNumberForm {
    return this.updateMobileNumberForm;
  }

  get args() {
    return this.$appStore.state.ui.modals.twoStepVerificationWelcome.args;
  }

  @Watch('visible', { immediate: true })
  async visibleChanged(newVisible: boolean) {
    if (newVisible) {
      this.isFormValid = true;
      this.updateMobileNumberForm.updatedMobileNumber = makeFormField();
      this.$services.ui.setUpdatedMobileNumberOnLogin(false); // reset flag
    }

    // Check when user comes back from a phone error
    if(this.args?.error && this.args?.error != '') {
      this.isFormValid = false;
      this.onClickUpdateMobileNumber();
      this.updateMobileNumberForm.updatedMobileNumber = 
      { value: '',
        dirty: true, 
        formatted: null, 
        error: 'Number already in use. Try another?', 
        errorMode: ErrorMode.PlaceHolder,
        oldValue: this.args.phoneNumber 
      };
    }
  }

  mounted() {
  }

  onClickTextCodeToMobileNumber(): void {
    this.$services.ui.setUpdatedMobileNumberOnLogin(false);
  }

  onClickUpdateMobileNumber(): void {
    this.$services.ui.setUpdatedMobileNumberOnLogin(true)
  }

  onClose() {
    this.$services.ui.setLastSelectedElement('cmg-two-step-verification-welcome-modal');
    Vue.nextTick(() => {
      this.$services.ui.setModalVisibility('exitMobileVerificationModal', true, {
        onLeave: () => this.exitTwoStepVerifcationWelcomeModal()
      });
      this.$services.ui.focusDiv('.exit-mobile-verification-modal');
    });
  }

  exitTwoStepVerifcationWelcomeModal() {
    this.args?.onFailure();
    this.$services.ui.setModalVisibility('twoStepVerificationWelcome', false);
    this.$services.ui.setModalVisibility('signIn', false);
  }

  async closeVerifyModal(): Promise<void> {
    this.$services.ui.setModalVisibility('verifyMobileNumber', false);
  }

  async onSendCode() {
    if (this.selectedUpdateMobileNumber) {
      this.validateMobileNumber();
      if (!this.isFormValid) {
        return;
      }
    }
    
    this.verifyMobileNumberModal();
  }

  verifyMobileNumberModal(): void {
    this.$services.ui.setModalVisibility('verifyMobileNumber', true, { 
      email: this.args?.email ?? '', 
      phoneNumber: this.selectedUpdateMobileNumber ? this.form.updatedMobileNumber.value : this.args?.phoneNumber ?? '',
      oldPhoneNumber: this.args?.phoneNumber ?? '',
      requiresPasswordVerification: this.args?.requiresPasswordVerification ? true : false,
      requiresSignOutOnFailure: true,
      onSuccess: this.args?.onSuccess ?? this.closeVerifyModal,
      onFailure: () => {
        this.$services.ui.setModalVisibility('verifyMobileNumber', false);
      }
    });
    this.$services.ui.setModalVisibility('twoStepVerificationWelcome', false, this.args);
    Vue.nextTick(() => {
      this.$services.ui.focusDiv('.cmg-verify-mobile-number-modal');
    });
  }

  validateMobileNumber(): void {
    this.isFormValid = true;
    if (this.updateMobileNumberForm.updatedMobileNumber.value.length == 0) {
      this.updateMobileNumberForm.updatedMobileNumber.error = 'Mobile Number is Required';
      this.updateMobileNumberForm.updatedMobileNumber.errorMode = ErrorMode.PlaceHolder;
    } else {
      this.updateMobileNumberForm.updatedMobileNumber.error = !isValidPhoneNumber(this.updateMobileNumberForm.updatedMobileNumber.value, this.currentCountry) 
        ? `Mobile number is invalid. Enter a valid ${this.currentCountry === CountryCode.GB ? 'UK ': ''}mobile number that can accept SMS mesasges.`
        : '';
      this.updateMobileNumberForm.updatedMobileNumber.errorMode = ErrorMode.BelowField;
    }
    this.isFormValid = !this.updateMobileNumberForm.updatedMobileNumber.error;
  }

  sanitizePhoneNumber(value: string): string {
    return this.currentCountry !== CountryCode.GB ? stripLeadingOnesAndZeros(stripNonNumerics(value)) : stripNonNumerics(value);
  }

  formatPhoneNumber(value: string): string {
    return formatPhoneNumber(value, this.currentCountry);
  }

  onClickContactCustomerCare() {
    this.$services.analytics.send(new TwoStepVerificationEvent(AnalyticsEventType.TwoStepVerificationGetSupport));
  }

  onUpdatePhoneNumberInput() {
    this.updateMobileNumberForm.updatedMobileNumber.error = '';

  }
}
